import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { adminRolesApi } from "@/lib/api";
import type { OperatorRole } from "@/lib/types";

export default function RoleDetail() {
  const { role } = useParams<{ role: string }>();
  const navigate = useNavigate();

  if (!role) {
    return <ErrorState error={new Error("Unknown role")} />;
  }

  return (
    <RequirePermission codes="role.manage">
      <RoleEditor
        role={role}
        onClose={() => navigate("/admin/roles")}
      />
    </RequirePermission>
  );
}

function RoleEditor({ role, onClose }: { role: OperatorRole; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const roleQ = useQuery({
    queryKey: ["admin", "roles", role],
    queryFn: () => adminRolesApi.getRole(role),
  });
  const permsQ = useQuery({
    queryKey: ["admin", "permissions"],
    queryFn: () => adminRolesApi.listPermissions(),
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (roleQ.data) setSelected(new Set(roleQ.data.permissions));
  }, [roleQ.data?.permissions.join(",")]);

  const mut = useMutation({
    mutationFn: () =>
      adminRolesApi.setRolePermissions(role, { permissions: [...selected] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "roles"] });
    },
  });

  const groups = useMemo(() => {
    if (!permsQ.data) return [];
    const buckets: Record<string, typeof permsQ.data> = {};
    for (const p of permsQ.data) {
      const area = p.code.split(".")[0] ?? "other";
      (buckets[area] ||= []).push(p);
    }
    return Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
  }, [permsQ.data]);

  if (roleQ.isLoading || permsQ.isLoading) return <LoadingState />;
  if (roleQ.isError) return <ErrorState error={roleQ.error} />;
  if (permsQ.isError) return <ErrorState error={permsQ.error} />;

  const role_ = roleQ.data!;
  const original = new Set(role_.permissions);
  const dirty =
    selected.size !== original.size || [...selected].some((c) => !original.has(c));

  function toggle(code: string, on: boolean) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (on) next.add(code); else next.delete(code);
      return next;
    });
  }

  return (
    <>
      <PageHeader
        eyebrow={`admin · role · ${role}`}
        title={role_.name}
        description={t("roleDetail.description", { count: role_.user_count })}
        actions={
          <Link to="/admin/roles" className="btn" onClick={(e) => { e.preventDefault(); onClose(); }}>
            {t("roleDetail.allRoles")}
          </Link>
        }
      />

      <div className="card p-5 space-y-6">
        {groups.map(([area, perms]) => (
          <fieldset key={area} className="space-y-2">
            <legend className="font-mono text-xs uppercase tracking-widest text-text-muted">
              {area}
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {perms.map((p) => (
                <label key={p.code} className="flex items-start gap-2 text-sm py-1.5 px-2 rounded hover:bg-surface-hover">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selected.has(p.code)}
                    onChange={(e) => toggle(p.code, e.target.checked)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-text-primary">{p.code}</div>
                    {p.description && (
                      <div className="text-xs text-text-muted">{p.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </fieldset>
        ))}

        {mut.isError && <ErrorState error={mut.error} />}

        <div className="flex items-center gap-2 pt-3 border-t border-border">
          <button
            className="btn btn-primary"
            disabled={!dirty || mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? t("common.saving") : t("common.save")}
          </button>
          <button
            className="btn"
            disabled={!dirty || mut.isPending}
            onClick={() => setSelected(new Set(role_.permissions))}
          >
            {t("common.reset")}
          </button>
          {role === "ADMIN" && (
            <p className="ml-auto text-xs text-text-muted">
              {t("roleDetail.adminLock1")} <span className="font-mono">user.manage</span>{" "}
              {t("roleDetail.adminLock2")}
              <span className="font-mono"> role.manage</span> {t("roleDetail.adminLock3")}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
